import { rectCenter, rotatePoint } from './geometry.ts'
import type { Point, Rect } from './types.ts'

export type Handle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w'

export const HANDLES: Handle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w']

const MIN_SIZE = 1

/** Unit position of a handle within a bounding box, 0..1 on each axis. */
export function handleUnit(h: Handle): Point {
  const x = h.includes('w') ? 0 : h.includes('e') ? 1 : 0.5
  const y = h.includes('n') ? 0 : h.includes('s') ? 1 : 0.5
  return { x, y }
}

export function handleOpposite(h: Handle): Handle {
  const map: Record<Handle, Handle> = {
    nw: 'se', n: 's', ne: 'sw', e: 'w', se: 'nw', s: 'n', sw: 'ne', w: 'e',
  }
  return map[h]
}

export function handlePoint(r: Rect, h: Handle): Point {
  const u = handleUnit(h)
  return { x: r.x + r.w * u.x, y: r.y + r.h * u.y }
}

export interface ResizeOptions {
  /** Preserve the original aspect ratio (shift). */
  aspect?: boolean
  /** Resize about the box center instead of the opposite handle (alt). */
  fromCenter?: boolean
}

/**
 * Resize an unrotated rect by dragging `handle` to `to`, holding the opposite
 * handle fixed. Works in the box's own axis-aligned frame.
 */
export function resizeRect(
  orig: Rect,
  handle: Handle,
  to: Point,
  opts: ResizeOptions = {},
): Rect {
  const u = handleUnit(handle)
  const anchor = opts.fromCenter
    ? rectCenter(orig)
    : handlePoint(orig, handleOpposite(handle))

  const movesX = u.x !== 0.5
  const movesY = u.y !== 0.5

  const scale = opts.fromCenter ? 2 : 1
  let w = movesX ? Math.abs(to.x - anchor.x) * scale : orig.w
  let h = movesY ? Math.abs(to.y - anchor.y) * scale : orig.h

  if (opts.aspect && orig.w > 0 && orig.h > 0) {
    const ratio = orig.w / orig.h
    if (movesX && movesY) {
      // Grow along whichever axis the pointer pushed further.
      if (w / ratio > h) h = w / ratio
      else w = h * ratio
    } else if (movesX) {
      h = w / ratio
    } else if (movesY) {
      w = h * ratio
    }
  }

  w = Math.max(MIN_SIZE, w)
  h = Math.max(MIN_SIZE, h)

  if (opts.fromCenter) {
    return { x: anchor.x - w / 2, y: anchor.y - h / 2, w, h }
  }

  // Keep the anchor put, flipping the box when dragged past it. On an axis the
  // handle does not move, aspect lock may still have changed the size — grow it
  // symmetrically there so the box does not drift sideways.
  const center = rectCenter(orig)
  const x = movesX
    ? to.x < anchor.x
      ? anchor.x - w
      : anchor.x
    : center.x - w / 2
  const y = movesY
    ? to.y < anchor.y
      ? anchor.y - h
      : anchor.y
    : center.y - h / 2

  return { x, y, w, h }
}

/**
 * Resize a rotated node. The drag is interpreted in the node's unrotated frame,
 * then the result is translated so the world position of the fixed anchor corner
 * does not move — without that correction a rotated box walks away as you drag.
 */
export function resizeRotated(
  orig: Rect,
  rotation: number,
  handle: Handle,
  toWorld: Point,
  opts: ResizeOptions = {},
): Rect {
  if (rotation === 0) return resizeRect(orig, handle, toWorld, opts)

  const center = rectCenter(orig)
  const local = rotatePoint(toWorld, center, -rotation)
  const next = resizeRect(orig, handle, local, opts)

  const anchor = opts.fromCenter
    ? center
    : handlePoint(orig, handleOpposite(handle))
  const anchorWorld = opts.fromCenter ? center : rotatePoint(anchor, center, rotation)
  const anchorAfter = opts.fromCenter
    ? rectCenter(next)
    : rotatePoint(handlePoint(next, handleOpposite(handle)), rectCenter(next), rotation)

  return {
    ...next,
    x: next.x + (anchorWorld.x - anchorAfter.x),
    y: next.y + (anchorWorld.y - anchorAfter.y),
  }
}

/** Map a rect from one bounding box into another, used for group resize. */
export function mapRect(r: Rect, from: Rect, to: Rect): Rect {
  const sx = from.w === 0 ? 1 : to.w / from.w
  const sy = from.h === 0 ? 1 : to.h / from.h
  return {
    x: to.x + (r.x - from.x) * sx,
    y: to.y + (r.y - from.y) * sy,
    w: Math.max(MIN_SIZE, r.w * sx),
    h: Math.max(MIN_SIZE, r.h * sy),
  }
}

export function angleTo(center: Point, p: Point): number {
  return Math.atan2(p.y - center.y, p.x - center.x)
}

/** Snap to 15 degree increments. */
export function snapAngle(angle: number): number {
  const step = Math.PI / 12
  return Math.round(angle / step) * step
}

/** Pivot snap targets in box-relative units: the center and the corners. */
export const PIVOT_SNAPS: Point[] = [
  { x: 0.5, y: 0.5 },
  { x: 0, y: 0 },
  { x: 1, y: 0 },
  { x: 1, y: 1 },
  { x: 0, y: 1 },
]

type Pivoted = Rect & { rotation: number; pivot?: Point }

export function pivotToWorld(n: Pivoted): Point {
  const u = n.pivot ?? { x: 0.5, y: 0.5 }
  return rotatePoint({ x: n.x + n.w * u.x, y: n.y + n.h * u.y }, rectCenter(n), n.rotation)
}

export function pivotFromWorld(n: Pivoted, p: Point): Point {
  const local = rotatePoint(p, rectCenter(n), -n.rotation)
  return {
    x: n.w === 0 ? 0.5 : (local.x - n.x) / n.w,
    y: n.h === 0 ? 0.5 : (local.y - n.y) / n.h,
  }
}

/**
 * Turn a node by `delta` about a world point. Nodes render rotated about their
 * own center, so turning about anything else also moves the box.
 */
export function rotateAbout(
  n: Pivoted,
  pivot: Point,
  delta: number,
): { x: number; y: number; rotation: number } {
  const c = rotatePoint(rectCenter(n), pivot, delta)
  return { x: c.x - n.w / 2, y: c.y - n.h / 2, rotation: n.rotation + delta }
}
